import React, { useEffect, useState } from 'react'
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, TextField } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useCreateBrandMutation, useUpdateBrandMutation, useGetBrandQuery } from '../../../redux/growthosBrandsApiSlice'
import { SEGMENTS } from '../constants'

const emptyForm = {
  brandName: '',
  segment: '',
  vertical: '',
  city: '',
  proshipBrandId: '',
  salesPocId: '',
  maxSowManual: '',
  isExcluded: false,
  isHiddenFromDisplay: false,
}

/** Create/edit a GrowthOS brand. editingBrandId null => create mode. */
export default function BrandFormDialog({ open, onClose, editingBrandId }) {
  const { enqueueSnackbar } = useSnackbar()
  const { data: existing } = useGetBrandQuery(editingBrandId, { skip: !editingBrandId })
  const [createBrand, createState] = useCreateBrandMutation()
  const [updateBrand, updateState] = useUpdateBrandMutation()
  const [form, setForm] = useState(emptyForm)
  const [nameErr, setNameErr] = useState(false)

  useEffect(() => {
    if (editingBrandId && existing) {
      setForm({
        brandName: existing.brandName || '',
        segment: existing.segment || '',
        vertical: existing.vertical || '',
        city: existing.city || '',
        proshipBrandId: existing.proshipBrandId || '',
        salesPocId: existing.salesPocId || '',
        maxSowManual: existing.maxSowManual ?? '',
        // GrowthOsBrandResponse serializes these without the "is" prefix (Jackson strips it
        // off primitive-boolean getters) — the create/update request body keys are unaffected
        // since GrowthOsBrandRequest uses boxed Boolean, whose getter keeps the full name.
        isExcluded: !!existing.excluded,
        isHiddenFromDisplay: !!existing.hiddenFromDisplay,
      })
    } else if (!editingBrandId) {
      setForm(emptyForm)
    }
  }, [editingBrandId, existing])

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))
  const setBool = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.checked }))

  const submit = async () => {
    if (!form.brandName.trim()) { setNameErr(true); return }
    const body = {
      brandName: form.brandName.trim(),
      segment: form.segment || undefined,
      vertical: form.vertical || undefined,
      city: form.city || undefined,
      proshipBrandId: form.proshipBrandId || undefined,
      salesPocId: form.salesPocId || undefined,
      maxSowManual: form.maxSowManual === '' ? undefined : Number(form.maxSowManual),
      isExcluded: form.isExcluded,
      isHiddenFromDisplay: form.isHiddenFromDisplay,
    }
    try {
      if (editingBrandId) {
        await updateBrand({ id: editingBrandId, body }).unwrap()
        enqueueSnackbar('Brand updated.', { variant: 'success' })
      } else {
        await createBrand(body).unwrap()
        enqueueSnackbar('Brand created.', { variant: 'success' })
      }
      onClose && onClose()
    } catch (e) {
      enqueueSnackbar(e?.message || 'Save failed.', { variant: 'error' })
    }
  }

  const busy = createState.isLoading || updateState.isLoading

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>{editingBrandId ? 'Edit brand' : 'New brand'}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <TextField
          fullWidth required size="small" label="Brand name"
          value={form.brandName}
          onChange={(e) => { setNameErr(false); set('brandName')(e) }}
          error={nameErr}
          helperText={nameErr ? 'Brand name is required.' : ' '}
        />
        <TextField fullWidth select size="small" label="Segment" value={form.segment} onChange={set('segment')}>
          <MenuItem value="">—</MenuItem>
          {SEGMENTS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField fullWidth size="small" label="Vertical" value={form.vertical} onChange={set('vertical')} />
        <TextField fullWidth size="small" label="City" value={form.city} onChange={set('city')} />
        <TextField fullWidth size="small" label="ProShip Brand ID (merchantId)" value={form.proshipBrandId} onChange={set('proshipBrandId')} />
        <TextField fullWidth size="small" label="Sales POC ID" value={form.salesPocId} onChange={set('salesPocId')} />
        <TextField fullWidth size="small" type="number" label="Max SOW (manual override)" value={form.maxSowManual} onChange={set('maxSowManual')} />
        <FormControlLabel control={<Checkbox checked={form.isExcluded} onChange={setBool('isExcluded')} />} label="Excluded from analytics" />
        <FormControlLabel control={<Checkbox checked={form.isHiddenFromDisplay} onChange={setBool('isHiddenFromDisplay')} />} label="Hidden from display (masking only)" />
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" disabled={busy} onClick={submit} sx={{ textTransform: 'none' }}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
